import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حساب الرقم الهيدروجيني لمحاليل الأحماض - حاسبة pH دقيقة";
const DESCRIPTION =
  "حاسبة الرقم الهيدروجيني (pH) للأحماض والقواعد. احسب درجة الحموضة بسهولة باستخدام التركيز المولاري. أداة دقيقة للكيميائيين والطلاب مع شرح للمعادلات والأمثلة.";
const KEYWORDS =
  "حساب الرقم الهيدروجيني, حاسبة pH, درجة الحموضة, حساب pH الحمض والقاعدة, تركيز أيون الهيدروجين, قوانين الرقم الهيدروجيني, pH calculator arabic";
const OG_URL = "https://do-calculate.com/calculator/ar/ph-acid/";

// JSON-LD Schema for SEO
const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة الرقم الهيدروجيني (pH)",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "ScienceApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات العلمية",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة الرقم الهيدروجيني",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "ما هو الرقم الهيدروجيني (pH)؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "الرقم الهيدروجيني هو مقياس لدرجة حموضة أو قاعدية المحلول المائي، وتتراوح قيمته عادة بين 0 و14، حيث تكون 7 متعادلة، وأقل من 7 حمضية، وأكثر من 7 قاعدية.",
        },
      },
      {
        "@type": "Question",
        name: "كيف يتم حساب الرقم الهيدروجيني؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "يتم حساب الرقم الهيدروجيني باستخدام اللوغاريتم السالب لتركيز أيونات الهيدروجين في المحلول، بالمعادلة: pH = -log[H+].",
        },
      },
    ],
  },
];

function PHCalculator() {
  // States
  const [calcType, setCalcType] = useState("ph_from_h");
  const [inputValue, setInputValue] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculatePH = () => {
    try {
      setError("");
      const val = parseFloat(inputValue);

      if (isNaN(val) || val <= 0) {
        if (inputValue !== "") {
          setError("يرجى إدخال قيمة موجبة صحيحة أكبر من الصفر");
        }
        setResult(null);
        return;
      }

      let res = {};
      switch (calcType) {
        case "ph_from_h":
          // pH = -log10[H+]
          const ph = -Math.log10(val);
          const pohFromH = 14 - ph;
          res = {
            ph: ph.toFixed(4),
            poh: pohFromH.toFixed(4),
            h_conc: val,
            oh_conc: (Math.pow(10, -14) / val).toExponential(4),
            status: ph < 7 ? "حمضي" : ph > 7 ? "قاعدي" : "متعادل",
          };
          break;
        case "h_from_ph":
          // [H+] = 10^-pH
          const hConc = Math.pow(10, -val);
          const pohFromPh = 14 - val;
          res = {
            ph: val.toFixed(4),
            poh: pohFromPh.toFixed(4),
            h_conc: hConc.toExponential(4),
            oh_conc: (Math.pow(10, -14) / hConc).toExponential(4),
            status: val < 7 ? "حمضي" : val > 7 ? "قاعدي" : "متعادل",
          };
          break;
        default:
          break;
      }
      setResult(res);
    } catch (err) {
      setError("خطأ في العملية الحسابية");
      setResult(null);
    }
  };

  useEffect(() => {
    calculatePH();
  }, [calcType, inputValue]);

  const handleClear = () => {
    setInputValue("");
    setResult(null);
    setError("");
  };

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>حساب الرقم الهيدروجيني لمحاليل الأحماض</h1>
          </div>

          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                استخدم <strong>حاسبة الرقم الهيدروجيني</strong> لتحويل تركيز أيونات الهيدروجين إلى قيمة pH أو العكس. هذه الأداة ضرورية لطلاب الكيمياء والمهنيين لتحديد مدى حموضة أو قاعدية المحاليل المائية بدقة عالية.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>أدخل البيانات للحساب</h2>
              </div>

              <div className="row">
                <div className="col-sm-12">
                  <label >نوع العملية</label>
                  <select
                    className="form-select"
                    value={calcType}
                    onChange={(e) => {
                      setCalcType(e.target.value);
                      setInputValue("");
                    }}
                  >
                    <option value="ph_from_h">حساب pH من تركيز [H+]</option>
                    <option value="h_from_ph">حساب [H+] من قيمة pH</option>
                  </select>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-sm-12">
                  <label >
                    {calcType === "ph_from_h" ? "تركيز أيون الهيدروجين [H+] (mol/L)" : "قيمة الرقم الهيدروجيني (pH)"}
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={calcType === "ph_from_h" ? "مثال: 0.001" : "مثال: 7"}
                  />
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button onClick={handleClear} className="btn btn-secondary" style={{ padding: "10px 30px" }}>
                  امسح
                </button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>تنبيه:</strong> {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>النتيجة النهائية:</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>المقياس</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>القيمة</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>الرقم الهيدروجيني (pH)</td>
                          <td style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", color: "#1a5f3f" }}>
                            {result.ph}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>الرقم الهيدروكسيلي (pOH)</td>
                          <td style={{ textAlign: "center" }}>{result.poh}</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>حالة المحلول</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>{result.status}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>التراكيز المولارية</strong>
                      </div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                          <li>🧪 <strong>تركيز [H+]:</strong> {result.h_conc} mol/L</li>
                          <li>🧪 <strong>تركيز [OH-]:</strong> {result.oh_conc} mol/L</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>

        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>ما هو الرقم الهيدروجيني pH؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  الرقم الهيدروجيني أو الأس الهيدروجيني (pH) هو لوغاريتم تركيز أيون الهيدروجين في المحلول، ويستخدم للتعبير عن مدى حموضة أو قاعدية مادة ما. تبدأ القيم من 0 وتصل إلى 14. 
                                  </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>المحاليل الحمضية:</strong> هي التي يكون الرقم الهيدروجيني لها أقل من 7.</li>
                  <li><strong>المحاليل المتعادلة:</strong> مثل الماء النقي، ويكون الرقم الهيدروجيني لها 7 تماماً.</li>
                  <li><strong>المحاليل القاعدية (القلوية):</strong> هي التي يكون الرقم الهيدروجيني لها أكثر من 7.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>طريقة حساب الرقم الهيدروجيني</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  تعتمد الحسابات الكيميائية للرقم الهيدروجيني على القوانين التالية:
                </p>
                <div className="alert alert-info" style={{ direction: "ltr", textAlign: "left" }}>
                  1. pH = -log₁₀[H⁺]<br />
                  2. pOH = -log₁₀[OH⁻]<br />
                  3. pH + pOH = 14 (عند درجة حرارة 25 مئوية)
                </div>
                <p style={{ lineHeight: "1.8" }}>
                  حيث يمثل [H⁺] تركيز أيونات الهيدروجين بالمول لكل لتر، بينما يمثل [OH⁻] تركيز أيونات الهيدروكسيد.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>أهمية قياس الـ pH في حياتنا</strong>
              </div>
              <div className="info-section-body">
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>في الطب:</strong> الحفاظ على حموضة الدم عند مستوى 7.4 ضروري جداً للحياة.</li>
                  <li><strong>في الزراعة:</strong> تؤثر حموضة التربة بشكل مباشر على نمو المحاصيل وامتصاص المغذيات.</li>
                  <li><strong>في الصناعة:</strong> تحتاج محطات معالجة المياه وصناعة الأغذية لمراقبة دقيقة للـ pH.</li>
                  <li><strong>في حمامات السباحة:</strong> لضمان فعالية الكلور وسلامة بشرة السباحين.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>أمثلة عملية لحساب الرقم الهيدروجيني</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px" }}>مثال 1: حساب pH حمض الهيدروكلوريك</h3>
                <p>إذا كان تركيز أيونات الهيدروجين في محلول هو 0.01 مول/لتر، فما هو الرقم الهيدروجيني؟</p>
                <p>الحل: pH = -log(0.01) = 2. المحلول حمضي قوي.</p>

                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>مثال 2: حساب التركيز من الـ pH</h3>
                <p>إذا كان الرقم الهيدروجيني لمحلول هو 10، فما هو تركيز أيونات الهيدروجين؟</p>
                <p>الحل: [H⁺] = 10^-10 مول/لتر. المحلول قاعدي.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>نصائح للاستخدام الصحيح</strong>
              </div>
              <div className="info-section-body">
                <ul style={{ lineHeight: "1.8" }}>
                  <li>تأكد من إدخال التركيز بالوحدة الصحيحة (مول/لتر).</li>
                  <li>في حال التعامل مع أحماض ضعيفة، يجب استخدام ثابت التفكك (Ka) قبل حساب التركيز النهائي.</li>
                  <li>تذكر أن المقياس لوغاريتمي، أي أن تغيير درجة واحدة في pH يعني تغييراً بمقدار 10 أضعاف في التركيز.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>الأسئلة الشائعة (FAQ)</strong>
              </div>
              <div className="info-section-body">
                <div className="mb-3">
                  <strong>1. هل يمكن أن تكون قيمة pH أقل من صفر؟</strong>
                  <p>نعم، في حالات الأحماض المركزة جداً والقوية، يمكن أن تكون القيمة سالبة، رغم أن المقياس الشائع هو من 0 إلى 14.</p>
                </div>
                <div className="mb-3">
                  <strong>2. ما هو الجهاز المستخدم لقياس الرقم الهيدروجيني؟</strong>
                  <p>يستخدم جهاز يسمى "مقياس الـ pH" (pH meter) أو ورق دوار الشمس والكواشف اللونية لتقدير القيمة بسرعة.</p>
                </div>
                <div className="mb-3">
                  <strong>3. كيف يؤثر التخفيف على الـ pH؟</strong>
                  <p>عند تخفيف حمض بالماء، يقل تركيز أيونات الهيدروجين وبالتالي تزداد قيمة pH مقتربة من الرقم 7.</p>
                </div>
                <div className="mb-3">
                  <strong>4. ما هي قيمة pH للماء المقطر؟</strong>
                  <p>قيمة pH للماء المقطر النقي هي 7، مما يجعله متعادلاً، لكن تعرضه للهواء قد يجعله حمضياً قليلاً بسبب ذوبان ثاني أكسيد الكربون.</p>
                </div>
                <div className="mb-3">
                  <strong>5. لماذا يعتبر الرقم الهيدروجيني للدم ثابتاً؟</strong>
                  <p>يحتوي جسم الإنسان على أنظمة "محلول منظم" (Buffer systems) تحافظ على استقرار الـ pH في الدم ضمن نطاق ضيق جداً.</p>
                </div>
                <div className="mb-3">
                  <strong>6. كيف نحسب pOH من pH؟</strong>
                  <p>ببساطة نقوم بطرح قيمة pH من الرقم 14، فإذا كان pH يساوي 4 فإن pOH يساوي 10.</p>
                </div>
                <div className="mb-3">
                  <strong>7. هل درجة الحرارة تؤثر على القياس؟</strong>
                  <p>نعم، تتأثر قيمة pH بدرجة الحرارة لأن تفكك الماء يتغير بتغير الحرارة، لذا يفضل القياس عند درجة 25 مئوية.</p>
                </div>
                <div className="mb-3">
                  <strong>8. ما الفرق بين الحمض القوي والحمض الضعيف؟</strong>
                  <p>الحمض القوي يتأين بالكامل في الماء معطياً كل أيونات الهيدروجين، بينما الحمض الضعيف يتأين جزئياً فقط.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PHCalculator;